<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import CopySvg from '@/assets/svg/conversations/copy-01.svg?component';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { ErrorService } from '@/core/shared/errors/error.service';

const dialogService = new DialogService();

const isOpen = computed(() => dialogService.jobMatchReportSuccessModalProps.isOpen);
const referenceId = computed(() =>
  dialogService.jobMatchReportSuccessModalProps.isOpen
    ? dialogService.jobMatchReportSuccessModalProps.referenceId
    : '',
);

const isCopied = ref(false);
let copyTimeout: ReturnType<typeof setTimeout> | undefined;

function close() {
  dialogService.jobMatchReportSuccessModalProps = { isOpen: false };
}

function copyReferenceId() {
  if (referenceId.value) {
    navigator.clipboard
      .writeText(referenceId.value)
      .then(() => {
        isCopied.value = true;
        if (copyTimeout) {
          clearTimeout(copyTimeout);
        }
        copyTimeout = setTimeout(() => {
          isCopied.value = false;
          copyTimeout = undefined;
        }, 1500);
      })
      .catch((error) => {
        ErrorService.captureException(error);
      });
  }
}

onUnmounted(() => {
  if (copyTimeout) {
    clearTimeout(copyTimeout);
  }
});
</script>

<template>
  <BaseModal
    :model-value="isOpen"
    :show-heading="false"
    :show-cancel-button="false"
    submit-button-text="Close"
    @submit="close"
    @cancel="close"
  >
    <template #header>
      <div>
        <div class="text-center font-serif text-2xl font-black text-copilot">
          Thank you for your request
        </div>
      </div>
    </template>

    <div class="mb-6 mt-4 flex flex-col items-center">
      <p class="mb-4 text-center text-base text-shade-800">
        Your Job Match Report is being generated and will be emailed to you shortly. Please keep
        your reference ID for future inquiries.
      </p>
      <div class="mt-2 rounded-lg bg-tint-40 px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-sm text-shade-600">
              Reference ID: <span class="font-bold">{{ referenceId }}</span>
            </span>
            <button
              class="relative ml-2 flex items-center justify-center rounded-full p-1 transition-colors"
              @click="copyReferenceId"
            >
              <CopySvg class="h-4 w-4 cursor-pointer text-shade-800 hover:text-shade-900" />
              <transition name="fade">
                <div
                  v-if="isCopied"
                  class="absolute -top-8 left-1/2 z-50 -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white"
                >
                  Copied!
                </div>
              </transition>
            </button>
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <div class="flex justify-center">
        <button
          class="rounded-lg bg-copilot px-6 py-2 text-sm font-medium text-white hover:bg-copilot/90"
          @click="close"
        >
          Close
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
